/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import {
    useMutation,
    useQuery,
    useQueryClient,
    keepPreviousData,
} from '@tanstack/react-query';

import { SearchActions } from 'sarsaparilla';

import { useDebounce } from 'shared-ui/utils/useDebounce';

import { getSearchSuggestions } from 'shared-ui/api/search/search.api';
import type { HistorySuggestion } from 'shared-ui/api/search/search.types';
import { transformSearchSuggestionResult } from './QuickSearch.transforms';

export const SEARCH_SUGGESTIONS_KEY = 'searchSuggestions';
export const SEARCH_HISTORY_KEY = 'searchHistory';

export function useSearchSuggestionsQuery(query: string) {
    const debouncedQuery = useDebounce(query, 200);

    return useQuery({
        queryKey: [SEARCH_SUGGESTIONS_KEY, debouncedQuery],
        queryFn: async ({ signal }) => getSearchSuggestions(debouncedQuery, signal),
        staleTime: Infinity,
        retry: 1,
        enabled: !!debouncedQuery,
        placeholderData: keepPreviousData,
        select: transformSearchSuggestionResult,
    });
}

/**
 * The user's recent searches — from localStorage, merged with the API's copy when
 * signed in. Keyed by account so signing in or out loads the right history.
 * Always stale, since other search boxes on the page can add to it.
 */
export function useSearchHistoryQuery({
    accountId,
    enabled = true,
}: {
    accountId?: string | null;
    enabled?: boolean;
}) {
    return useQuery({
        queryKey: [SEARCH_HISTORY_KEY, accountId ?? null],
        queryFn: async (): Promise<HistorySuggestion[]> =>
            SearchActions.loadSearchHistoryItems(),
        staleTime: 0,
        retry: false,
        enabled,
    });
}

export function useClearSearchHistoryMutation() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async () => SearchActions.deleteSearchHistoryItems(),
        onSuccess: () => {
            // Set rather than refetch: sarsaparilla caches history in memory for a
            // couple of seconds and would hand back what was just cleared
            queryClient.setQueriesData({ queryKey: [SEARCH_HISTORY_KEY] }, []);
        },
        onError: (e) => {
            // localStorage is cleared before the API call, so reload whatever survived
            // eslint-disable-next-line no-console
            console.log('error deleting history', e);
            queryClient.invalidateQueries({ queryKey: [SEARCH_HISTORY_KEY] });
        },
    });
}
